import type { Combatant, HpHiddenCombatant, ProjectedCombatant } from "@lancer-vtt/domain";
import { isMaskedCombatant, isHpVisible } from "../disclosure";

/**
 * Narrow a rendered {@link ProjectedCombatant} back to the real `Combatant` shape for an
 * **own-unit-only** surface (status editing, base authoring, the activate/action bar, door/conceal
 * toggles).
 * Disclosure redaction only ever targets a HOSTILE the viewer doesn't control — every
 * call site of this helper is already gated on `canControl`/`canEditBase`/`canConceal` (GM-only or
 * own-unit-only), which the projection's owner/GM exemption guarantees is never masked or
 * HP-hidden. Verified at runtime rather than a blind cast, so a genuine invariant break (a masked
 * combatant somehow reaching an own-unit surface) throws loudly instead of silently reading
 * `undefined` off a stripped field.
 */
export function assertUnmasked(c: ProjectedCombatant): Combatant {
  if (isMaskedCombatant(c)) throw new Error(`combatant ${c.id} reached an own-unit-only surface masked at "${c.disclosure}"`);
  if (!isHpVisible(c.current)) throw new Error(`combatant ${c.id} reached an own-unit-only surface with HP hidden`);
  // Both throws above rule out `MaskedCombatant`/`HpHiddenCombatant` at runtime; TS's narrowing
  // doesn't follow `isHpVisible`'s internal check back onto `c`'s own union member, so the cast
  // documents what the two guards already verified rather than re-deriving it structurally.
  return c as Combatant;
}

/**
 * Reinterpret an ALREADY-unmasked `Combatant | HpHiddenCombatant` as a full `Combatant` for a
 * consumer that never reads live current-HP (action enumeration, targeting choosers). Safe by
 * construction: `HpHiddenCombatant` (`showEnemyHp` off) differs from `Combatant` in exactly
 * that one field, never the frozen base/loadout/other `current` fields these consumers read. Call
 * only after the masked ({@link isMaskedCombatant}) branch has already returned — a masked variant
 * genuinely lacks the base/loadout data this would otherwise misrepresent.
 */
export function widenHpHidden(c: Combatant | HpHiddenCombatant): Combatant {
  return c as Combatant;
}

/**
 * Does this NPC's PLAY inspector mount the **control panel** rather than the read-only dossier?
 *
 * Routed on the projection's SHAPE, not on control alone: a control grant without the visibility
 * grant (`decisions/0179`) leaves a player controlling an NPC that is still masked or HP-hidden for
 * them, and the control panel reads fields a masked combatant does not carry ({@link assertUnmasked}
 * throws). So control is necessary but not sufficient: the viewer must also receive the unit
 * unmasked with HP visible. A controlled-but-masked unit gets the dossier with the granted turn
 * strip on its state line. The window chrome no longer depends on it: every NPC sheet is titled
 * `NPC · NAME` and opens at the unit sheet's one default size (`inspectorSpec`).
 */
export function mountsNpcControl(c: ProjectedCombatant, opts: { isGm: boolean; canControl: boolean }): boolean {
  return (opts.isGm || opts.canControl) && receivesUnmasked(c);
}

/**
 * Did the viewer receive this unit unmasked with HP visible, the shape {@link assertUnmasked}
 * accepts? Control does not imply it: a CTRL grant without EYES (`decisions/0179`) leaves an
 * npc-side unit masked or HP-hidden for its driver, and `set_controller`'s `allegiance` can put
 * a mech or pilot on the npc side, where any player without the view grant gets it masked.
 */
export function receivesUnmasked(c: ProjectedCombatant): boolean {
  return !isMaskedCombatant(c) && isHpVisible(c.current);
}
